import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Table,
  Form,
  Modal,
} from "react-bootstrap";
import MUIDataTable from "mui-datatables";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import { Trash, GripVertical, Save, ArrowRight } from "react-bootstrap-icons";
import CCService from "../services/cc.service";
import ClienteService from "../services/cliente.service";
import UserService from "../services/user.service";
import EventBus from "../common/EventBus";
import "./ordenDeDia.css";

const OrdenDeDia = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allCuentas, setAllCuentas] = useState([]);
  const [diaFiltro, setDiaFiltro] = useState("Lunes");
  const [vendedorSeleccionado, setVendedorSeleccionado] = useState("");
  const [vendedores, setVendedores] = useState([]);
  const [clienteSeleccionadoId, setClienteSeleccionadoId] = useState(null);
  const [hayCambios, setHayCambios] = useState(false);
  const [modalConfirmacion, setModalConfirmacion] = useState({
    show: false,
    titulo: "",
    mensaje: "",
    onConfirmar: () => {},
  });

  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  const diasSemana = [
    { key: "Lunes", label: "Lunes", short: "Lun" },
    { key: "Martes", label: "Martes", short: "Mar" },
    { key: "Miércoles", label: "Miércoles", short: "Mié" },
    { key: "Jueves", label: "Jueves", short: "Jue" },
    { key: "Viernes", label: "Viernes", short: "Vie" },
    { key: "Sábado", label: "Sábado", short: "Sáb" },
    { key: "Dia Especial", label: "Día Especial", short: "Esp" },
    { key: "Lunes 2", label: "Lunes 2", short: "Lun 2" },
    { key: "Martes 2", label: "Martes 2", short: "Mar 2" },
    { key: "Miércoles 2", label: "Miércoles 2", short: "Mié 2" },
    { key: "Jueves 2", label: "Jueves 2", short: "Jue 2" },
    { key: "Viernes 2", label: "Viernes 2", short: "Vie 2" },
    { key: "Sábado 2", label: "Sábado 2", short: "Sáb 2" },
    { key: "Dia Especial 2", label: "Día Especial 2", short: "Esp 2" },
  ];

  const coincideDia = (diasDeVisita, diaBuscado) => {
    if (!diasDeVisita) return false;
    const diaBuscadoNorm = String(diaBuscado).toLowerCase().trim();
    const diasNorm = String(diasDeVisita).toLowerCase();
    if (diasNorm.trim() === diaBuscadoNorm) return true;
    const partes = diasNorm
      .split(/[,;]+/)
      .map((p) => p.trim())
      .filter((p) => p !== "");
    return partes.includes(diaBuscadoNorm);
  };

  const getIdCliente = (cuenta) => {
    if (!cuenta) return null;
    return (
      cuenta.clienteIdCliente ??
      cuenta.cliente?.idCliente ??
      cuenta.cliente?.IdCliente ??
      null
    );
  };

  useEffect(() => {
    UserService.getAdminBoard()
      .then((response) => {
        if (response.status === 200) {
          setIsAdmin(true);
          cargarDatos();
        } else {
          setIsAdmin(false);
          setLoading(false);
        }
      })
      .catch((error) => {
        setIsAdmin(false);
        setLoading(false);
        if (error.response && error.response.status === 401) {
          EventBus.dispatch("logout");
        }
      });
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const cuentasRes = await CCService.getTodos();
      const cuentas = cuentasRes?.data || [];

      const codigosVendedor = [
        ...new Set(
          cuentas
            .map((c) => c.cliente?.CodigoVendedor)
            .filter((v) => v !== undefined && v !== null && String(v).trim() !== "")
        ),
      ].sort((a, b) => String(a).localeCompare(String(b)));

      setAllCuentas(cuentas);
      setVendedores(codigosVendedor);
    } catch (error) {
      toast.error("Error al cargar los datos");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const cuentasFiltradas = useMemo(() => {
    if (!Array.isArray(allCuentas)) return [];
    let resultado = allCuentas;

    if (vendedorSeleccionado) {
      resultado = resultado.filter(
        (cuenta) => String(cuenta.cliente?.CodigoVendedor || "") === String(vendedorSeleccionado)
      );
    }

    return resultado.filter((cuenta) =>
      coincideDia(cuenta.cliente?.DiasDeVisita, diaFiltro)
    );
  }, [allCuentas, diaFiltro, vendedorSeleccionado]);

  const ordenConDatos = useMemo(() => {
    if (!vendedorSeleccionado) return [];
    return allCuentas
      .filter(
        (cuenta) =>
          String(cuenta.cliente?.CodigoVendedor || "") ===
          String(vendedorSeleccionado)
      )
      .filter((cuenta) => coincideDia(cuenta.cliente?.DiasDeVisita, diaFiltro))
      .filter((cuenta) => (cuenta.cliente?.OrdenDeDia ?? -1) >= 0)
      .sort((a, b) => (a.cliente?.OrdenDeDia ?? Infinity) - (b.cliente?.OrdenDeDia ?? Infinity))
      .map((cuenta) => ({
        idCliente: getIdCliente(cuenta),
        orden: cuenta.cliente?.OrdenDeDia ?? 0,
        codigo: cuenta.cliente?.Codigo || "-",
        codigoVendedor: cuenta.cliente?.CodigoVendedor || "-",
        diasDeVisita: cuenta.cliente?.DiasDeVisita || "-",
        razonSocial: cuenta.cliente?.RazonSocial || `Cliente #${getIdCliente(cuenta)}`,
      }));
  }, [allCuentas, diaFiltro, vendedorSeleccionado]);

  const idsEnOrden = useMemo(() => {
    return new Set(ordenConDatos.map((item) => item.idCliente));
  }, [ordenConDatos]);

  const actualizarCuenta = (idCliente, actualizacion) => {
    setAllCuentas((prev) =>
      prev.map((cuenta) => {
        const id = getIdCliente(cuenta);
        if (id !== Number(idCliente)) return cuenta;
        return {
          ...cuenta,
          cliente: {
            ...cuenta.cliente,
            ...actualizacion,
          },
        };
      })
    );
  };

  const clienteTieneDiaRestriccion = (idCliente) => {
    const id = Number(idCliente);
    const cuenta = allCuentas.find((c) => getIdCliente(c) === id);
    if (!cuenta) return false;
    return coincideDia(cuenta.cliente?.DiasDeVisita, diaFiltro);
  };

  const agregarCliente = (idCliente) => {
    const id = Number(idCliente);
    if (idsEnOrden.has(id)) {
      toast.warning("Este cliente ya está en el orden de visita");
      return;
    }

    if (!clienteTieneDiaRestriccion(id)) {
      toast.warning(`El cliente no tiene visitas el día ${diaFiltro}`);
      return;
    }

    const nuevoOrden = ordenConDatos.length > 0
      ? Math.max(...ordenConDatos.map((o) => o.orden)) + 1
      : 0;

    actualizarCuenta(id, { OrdenDeDia: nuevoOrden });
    setClienteSeleccionadoId(null);
    setHayCambios(true);
    toast.success("Cliente agregado al final del orden");
  };

  const mostrarConfirmacion = (titulo, mensaje, onConfirmar) => {
    setModalConfirmacion({ show: true, titulo, mensaje, onConfirmar });
  };

  const cerrarConfirmacion = () => {
    setModalConfirmacion((prev) => ({ ...prev, show: false }));
  };

  const cambiarVendedor = (nuevoVendedor) => {
    if (nuevoVendedor === vendedorSeleccionado) return;

    if (hayCambios) {
      mostrarConfirmacion(
        "Cambiar de vendedor",
        "Si cambiás de vendedor se va a perder el orden de visita no guardado. ¿Deseás continuar?",
        () => {
          setVendedorSeleccionado(nuevoVendedor);
          setClienteSeleccionadoId(null);
          setHayCambios(false);
          cerrarConfirmacion();
        }
      );
    } else {
      setVendedorSeleccionado(nuevoVendedor);
      setClienteSeleccionadoId(null);
    }
  };

  const cambiarDia = (nuevoDia) => {
    if (nuevoDia === diaFiltro) return;

    if (hayCambios) {
      mostrarConfirmacion(
        "Cambiar de día",
        "Si cambiás de día se va a perder el orden de visita no guardado. ¿Deseás continuar?",
        () => {
          setDiaFiltro(nuevoDia);
          setClienteSeleccionadoId(null);
          setHayCambios(false);
          cerrarConfirmacion();
        }
      );
    } else {
      setDiaFiltro(nuevoDia);
      setClienteSeleccionadoId(null);
    }
  };

  const agregarSeleccionado = () => {
    if (clienteSeleccionadoId === null) {
      toast.warning("Seleccioná un cliente de la lista de la izquierda");
      return;
    }
    agregarCliente(clienteSeleccionadoId);
  };

  const quitarCliente = (idCliente) => {
    actualizarCuenta(idCliente, { OrdenDeDia: null });
    setHayCambios(true);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(ordenConDatos);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);

    items.forEach((item, index) => {
      actualizarCuenta(item.idCliente, { OrdenDeDia: index });
    });
    setHayCambios(true);
  };

  const guardarOrden = async () => {
    if (ordenConDatos.length === 0) {
      toast.info("No hay clientes en el orden de visita");
      return;
    }

    const payload = ordenConDatos.map((item) => ({
      idCliente: item.idCliente,
      OrdenDeDia: item.orden,
    }));

    setSaving(true);
    try {
      await ClienteService.putOrdenDeDia(payload);
      setHayCambios(false);
      toast.success("Orden de visita guardado correctamente");
    } catch (error) {
      const mensaje =
        error.response?.data?.message ||
        error.message ||
        "Error al guardar el orden de visita";
      toast.error(mensaje);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      name: "clienteIdCliente",
      label: "ID",
      options: { display: "excluded" },
    },
    {
      name: "cliente.Codigo",
      label: "Código",
      options: {
        setCellProps: () => ({ style: { whiteSpace: "nowrap", fontWeight: 500 } }),
      },
    },
    {
      name: "cliente.RazonSocial",
      label: "Razón Social",
      options: {
        setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
      },
    },
    {
      name: "cliente.CodigoVendedor",
      label: "Vendedor",
      options: {
        setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
      },
    },
    {
      name: "cliente.DiasDeVisita",
      label: "Días de visita",
      options: {
        setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
      },
    },
  ];

  const options = {
    filterType: "checkbox",
    onRowsDelete: () => false,
    rowsPerPage: 100,
    rowsPerPageOptions: [10, 25, 50, 100],
    selectToolbarPlacement: "replace",
    responsive: "standard",
    searchOpen: true,
    searchAlwaysOpen: true,
    searchPlaceholder: "Buscar clientes...",
    filter: false,
    download: false,
    print: false,
    fixedHeader: true,
    enableNestedDataAccess: ".",
    selectableRows: "none",
    onRowClick: (rowData, rowMeta) => {
      const cuenta = cuentasFiltradas[rowMeta.dataIndex];
      const id = getIdCliente(cuenta);
      if (id !== null) setClienteSeleccionadoId(id);
    },
    setRowProps: (rowData, dataIndex) => {
      const cuenta = cuentasFiltradas[dataIndex];
      const id = getIdCliente(cuenta);
      const seleccionado = id !== null && id === clienteSeleccionadoId;
      return {
        onDoubleClick: () => {
          if (id !== null) agregarCliente(id);
        },
        style: {
          cursor: "pointer",
          backgroundColor: seleccionado ? "#e7f1ff" : undefined,
        },
      };
    },
  };

  const cardClientes = (
    <Card className="orden-card h-100">
      <Card.Header className="orden-card-header">
        <div>
          <h5 className="mb-0">Todos los clientes</h5>
          <small className="orden-card-count">
            {cuentasFiltradas.length} resultados
          </small>
        </div>
        <Badge pill bg="light" text="dark" className="orden-hint-badge">
          Seleccioná y tocá la flecha o hacé el doble click para agregar la fila
        </Badge>
      </Card.Header>
      <Card.Body className="p-0">
        {loading ? (
          <div className="orden-spinner">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : !vendedorSeleccionado ? (
          <div className="orden-empty">
            <p className="orden-empty-title">Seleccioná un vendedor</p>
            <p className="orden-empty-text">
              Elegí un vendedor del menú desplegable de arriba para cargar sus clientes.
            </p>
          </div>
        ) : (
          <MUIDataTable
            title={""}
            data={cuentasFiltradas}
            columns={columns}
            options={options}
          />
        )}
      </Card.Body>
    </Card>
  );

  const cardOrden = (
    <Card className={`orden-card h-100 ${hayCambios ? "orden-card-modified" : ""}`}>
      <Card.Header className="orden-card-header">
        <div>
          <h5 className="mb-0">Orden de visita - {diaFiltro}</h5>
          <small className="orden-card-count">
            {ordenConDatos.length} clientes
          </small>
        </div>
      </Card.Header>
      <Card.Body className="p-0 orden-derecha-body">
        {loading ? (
          <div className="orden-spinner">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : !vendedorSeleccionado ? (
          <div className="orden-empty">
            <p className="orden-empty-title">Seleccioná un vendedor</p>
            <p className="orden-empty-text">
              Elegí un vendedor del menú desplegable de arriba para cargar sus clientes.
            </p>
          </div>
        ) : ordenConDatos.length === 0 ? (
          <div className="orden-empty">
            <p className="orden-empty-title">Sin clientes asignados</p>
            <p className="orden-empty-text">
              Seleccioná un cliente de la izquierda y tocá la flecha para agregarlo.
            </p>
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Table responsive className="orden-table mb-0">
              <thead>
                <tr>
                  <th className="orden-col-handle"></th>
                  <th className="orden-col-orden">#</th>
                  <th>Código</th>
                  <th>Razón Social</th>
                  <th>Vendedor</th>
                  <th>Días</th>
                  <th className="orden-col-accion"></th>
                </tr>
              </thead>
              <Droppable droppableId="ordenDeDia">
                {(provided) => (
                  <tbody
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {ordenConDatos.map((item, index) => (
                      <Draggable
                        key={item.idCliente}
                        draggableId={String(item.idCliente)}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={
                              snapshot.isDragging
                                ? "orden-row-dragging"
                                : hayCambios
                                ? "orden-row-modified"
                                : ""
                            }
                          >
                            <td className="orden-col-handle">
                              <div
                                {...provided.dragHandleProps}
                                className="orden-drag-handle"
                              >
                                <GripVertical size={18} />
                              </div>
                            </td>
                            <td className="orden-col-orden">
                              <span className="orden-orden-badge">{index + 1}</span>
                            </td>
                            <td>{item.codigo}</td>
                            <td>{item.razonSocial}</td>
                            <td>{item.codigoVendedor}</td>
                            <td>{item.diasDeVisita}</td>
                            <td className="orden-col-accion">
                              <button
                                type="button"
                                className="orden-btn-quitar"
                                onClick={() => quitarCliente(item.idCliente)}
                                aria-label="Quitar cliente"
                              >
                                <Trash size={16} />
                              </button>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </Table>
          </DragDropContext>
        )}
      </Card.Body>
    </Card>
  );

  const botonFlecha = (
    <Button
      variant="primary"
      className="orden-btn-flecha"
      onClick={agregarSeleccionado}
      disabled={clienteSeleccionadoId === null}
      aria-label="Agregar cliente seleccionado"
    >
      <ArrowRight size={isTabletOrMobile ? 24 : 28} />
    </Button>
  );

  if (loading && !isAdmin) {
    return (
      <Container className="orden-loading">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="orden-loading">
        <h4>No tenés permisos para acceder a esta sección</h4>
      </Container>
    );
  }

  return (
    <Container fluid className="orden-de-dia-root">
      <Row className="align-items-center mb-3">
        <Col>
          <h3 className="orden-title">Orden de visita</h3>
          <p className="orden-subtitle">
            Armá el orden de visita arrastrando los clientes o haciendo doble clic para agregarlos.
          </p>
        </Col>
      </Row>

      <Row className="mb-3 justify-content-center align-items-center">
        <Col xs={12} md={6} lg={4} className="mb-2 mb-md-0">
          <Form.Select
            value={vendedorSeleccionado}
            onChange={(e) => cambiarVendedor(e.target.value)}
            className="orden-select-vendedor"
          >
            <option value="">Seleccioná un vendedor</option>
            {vendedores.map((vendedor) => (
              <option key={vendedor} value={vendedor}>
                Vendedor {vendedor}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <Form.Select
            value={diaFiltro}
            onChange={(e) => cambiarDia(e.target.value)}
            className="orden-select-vendedor"
          >
            {diasSemana.map((dia) => (
              <option key={dia.key} value={dia.key}>
                {dia.label}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {isTabletOrMobile ? (
        <>
          <Row className="g-3">
            <Col xs={12}>{cardClientes}</Col>
          </Row>
          {vendedorSeleccionado && (
            <Row className="justify-content-center">
              <Col xs="auto" className="orden-col-flecha">
                {botonFlecha}
              </Col>
            </Row>
          )}
          <Row className="g-3 mt-0">
            <Col xs={12}>{cardOrden}</Col>
          </Row>
        </>
      ) : (
        <Row className="g-3 align-items-stretch">
          <Col xs={12} lg={5}>{cardClientes}</Col>
          <Col
            xs={12}
            lg={2}
            className="d-flex align-items-center justify-content-center orden-col-flecha"
          >
            {vendedorSeleccionado && botonFlecha}
          </Col>
          <Col xs={12} lg={5}>{cardOrden}</Col>
        </Row>
      )}

      {isTabletOrMobile && (
        <div className="orden-mobile-hint">
          <ArrowRight size={16} /> Deslizá para ver más columnas
        </div>
      )}

      <div className="orden-actions-footer">
        <Button
          variant="primary"
          className="orden-btn-guardar"
          onClick={guardarOrden}
          disabled={saving || !vendedorSeleccionado}
        >
          {saving ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <Save size={18} />
          )}
          Guardar orden
        </Button>
      </div>

      <Modal
        show={modalConfirmacion.show}
        onHide={cerrarConfirmacion}
        centered
        backdrop="static"
      >
        <Modal.Header className="orden-modal-header">
          <Modal.Title>{modalConfirmacion.titulo}</Modal.Title>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={cerrarConfirmacion}
            className="orden-modal-close"
          >
            X
          </Button>
        </Modal.Header>
        <Modal.Body className="orden-modal-body">
          {modalConfirmacion.mensaje}
        </Modal.Body>
        <Modal.Footer className="orden-modal-footer">
          <Button variant="outline-secondary" onClick={cerrarConfirmacion}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              modalConfirmacion.onConfirmar();
            }}
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default OrdenDeDia;
